import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import Brandmark from "@/components/Brandmark";
import MockBadge from "@/components/MockBadge";
import { marketplace, ops } from "@/lib/api";
import { fmtTime, fmtUSD } from "@/lib/format";

/* Empty legs — TWO surfaces in one module:
   - default export  : the ops inventory view (auth-gated, /ops/empty-legs)
   - PublicEmptyLegs / PublicEmptyLegDetail : the PUBLIC marketplace
     (/empty-legs + /empty-legs/:slug — no auth; the server enforces THE WALL,
     so every leg is model class + route + date + seats + price only).
   The public pages also have a server-rendered HTML twin at
   /api/public/empty-legs/{slug}/html for crawlers / AI engines. */

// Acquisition attribution forwarded verbatim from the landing URL into the
// reserve / alert payloads (GROWTH attributes booked revenue to the channel).
const ATTRIBUTION_KEYS = ["utm_source", "utm_medium", "utm_campaign", "partner", "ref"];

function readAttribution() {
  if (typeof window === "undefined") return {};
  const sp = new URLSearchParams(window.location.search);
  const out = {};
  for (const k of ATTRIBUTION_KEYS) {
    const v = sp.get(k);
    if (v) out[k] = v;
  }
  return out;
}

// Shared input/button styles (matches the public Widget's inline-style pattern).
const INPUT_CLS = "w-full mono text-[12px] px-3 py-2.5";
const INPUT_STYLE = {
  background: "rgba(244,239,230,0.75)",
  border: "1px solid rgba(190,176,152,0.45)",
  color: "rgb(38,31,24)",
};
const BTN_PRIMARY_CLS =
  "w-full inline-flex items-center justify-center px-4 py-3 mono uppercase text-[11px] tracking-[0.14em] hover:opacity-90 disabled:opacity-60";
const BTN_PRIMARY_STYLE = {
  background: "linear-gradient(90deg, rgb(169,130,28), rgb(158,92,44))",
  color: "rgb(252,249,243)",
};
const BTN_SECONDARY_STYLE = {
  background: "rgba(244,239,230,0.9)",
  border: "1px solid rgba(190,176,152,0.6)",
  color: "rgb(38,31,24)",
};

// Static countdown to departure (perishable-inventory urgency on every card).
function countdownTo(departureAt) {
  const ms = new Date(departureAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return null;
  if (ms <= 0) return "Departed";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  if (d > 0) return `Departs in ${d}d ${h}h`;
  const m = Math.floor((ms % 3600000) / 60000);
  return `Departs in ${h}h ${m}m`;
}

/* ---------------------------------------------------------------------------
 * Reserve modal — email OR phone gate; 409 renders as scarcity messaging.
 * ------------------------------------------------------------------------- */
function ReserveModal({ leg, attribution, onClose }) {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    pax: 1,
    consent_marketing: false,
  });
  const [phase, setPhase] = useState("form"); // form | sending | done | conflict
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const set = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email && !form.phone) {
      setError("Enter an email or phone so we can hold this jet for you.");
      return;
    }
    setError(null);
    setPhase("sending");
    try {
      const r = await marketplace.reserve(leg.slug || leg.id, {
        full_name: form.full_name || null,
        email: form.email || null,
        phone: form.phone || null,
        pax: Number(form.pax) || 1,
        consent_marketing: !!form.consent_marketing,
        ...attribution,
      });
      setResult(r);
      setPhase("done");
    } catch (err) {
      if (err?.response?.status === 409) {
        setPhase("conflict");
      } else {
        setError(err?.response?.data?.detail || "Reserve failed — please try again.");
        setPhase("form");
      }
    }
  };

  return (
    <div
      data-testid="reserve-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(38,31,24,0.45)" }}
    >
      <div className="pj-card w-full max-w-md p-6" style={{ background: "rgb(var(--surface))" }}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="min-w-0">
            <div className="pj-label mb-1">· Reserve this empty leg</div>
            <div className="font-display text-[20px] truncate">
              {leg.from_airport} → {leg.to_airport}
            </div>
            <div className="mono text-[11px] text-[rgb(var(--dim))] mt-1">
              {leg.model_name} · {fmtUSD(leg.price_usd)} all-in
            </div>
          </div>
          <button
            type="button"
            data-testid="reserve-close"
            onClick={onClose}
            className="mono text-[11px] uppercase tracking-[0.14em] text-[rgb(var(--dim))] hover:text-[rgb(var(--fg))]"
          >
            Close
          </button>
        </div>

        {phase === "conflict" && (
          <div data-testid="reserve-conflict" className="mono text-[12px] leading-relaxed">
            Just missed — another traveler is holding this jet right now. Holds
            expire after 30 minutes, so it may free up. Set a price alert below
            and we&apos;ll email you the moment a matching leg opens.
          </div>
        )}

        {phase === "done" && result && (
          <div data-testid="reserve-done" className="mono text-[12px] leading-relaxed">
            Held for you{result.expires_at ? ` until ${fmtTime(result.expires_at)}` : ""}.
            {form.email
              ? " Check your inbox — your all-in quote is attached and bookable in one click."
              : " Our team will confirm by phone shortly."}
          </div>
        )}

        {(phase === "form" || phase === "sending") && (
          <form data-testid="reserve-form" onSubmit={submit} className="space-y-3">
            <input
              data-testid="reserve-name"
              className={INPUT_CLS}
              style={INPUT_STYLE}
              placeholder="Full name (optional)"
              value={form.full_name}
              onChange={set("full_name")}
            />
            <input
              data-testid="reserve-email"
              className={INPUT_CLS}
              style={INPUT_STYLE}
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={set("email")}
            />
            <input
              data-testid="reserve-phone"
              className={INPUT_CLS}
              style={INPUT_STYLE}
              placeholder="Phone (if no email)"
              value={form.phone}
              onChange={set("phone")}
            />
            <input
              data-testid="reserve-pax"
              className={INPUT_CLS}
              style={INPUT_STYLE}
              type="number"
              min={1}
              max={leg.seats || 20}
              value={form.pax}
              onChange={set("pax")}
            />
            <label className="flex items-start gap-2 mono text-[10px] text-[rgb(var(--dim))]">
              <input
                data-testid="reserve-consent"
                type="checkbox"
                className="mt-0.5"
                checked={form.consent_marketing}
                onChange={set("consent_marketing")}
              />
              <span>Email me future empty-leg deals (optional — opt out any time).</span>
            </label>
            {error && (
              <div data-testid="reserve-error" className="mono text-[11px]" style={{ color: "rgb(196,52,44)" }}>
                {error}
              </div>
            )}
            <button
              data-testid="reserve-submit"
              type="submit"
              disabled={phase === "sending"}
              className={BTN_PRIMARY_CLS}
              style={BTN_PRIMARY_STYLE}
            >
              {phase === "sending" ? "Holding…" : `Hold for ${fmtUSD(leg.price_usd)}`}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * Price-drop alert form — CONSENT-gated (CAN-SPAM/GDPR).
 * ------------------------------------------------------------------------- */
function PriceAlertForm({ prefill = {}, attribution }) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [phase, setPhase] = useState("idle"); // idle | sending | done
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) {
      setError("Enter your email to get price-drop alerts.");
      return;
    }
    if (!consent) {
      setError("Please confirm you'd like deal emails — you can opt out any time.");
      return;
    }
    setError(null);
    setPhase("sending");
    try {
      await marketplace.alert({
        email,
        consent: true,
        from_airport: prefill.from_airport || null,
        to_airport: prefill.to_airport || null,
        ...attribution,
      });
      setPhase("done");
    } catch (err) {
      setError(err?.response?.data?.detail || "Subscribe failed — please try again.");
      setPhase("idle");
    }
  };

  if (phase === "done") {
    return (
      <div data-testid="alert-done" className="pj-card p-4 mono text-[12px]">
        You&apos;re on the list — we&apos;ll email you when a matching deal opens or a
        price drops. One-click opt-out in every email.
      </div>
    );
  }

  return (
    <form data-testid="alert-form" onSubmit={submit} className="pj-card p-4 space-y-3">
      <div className="pj-label">· Price-drop alerts{prefill.from_airport ? ` · ${prefill.from_airport} → ${prefill.to_airport}` : ""}</div>
      <input
        data-testid="alert-email"
        className={INPUT_CLS}
        style={INPUT_STYLE}
        type="email"
        placeholder="you@example.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <label className="flex items-start gap-2 mono text-[10px] text-[rgb(var(--dim))]">
        <input
          data-testid="alert-consent"
          type="checkbox"
          className="mt-0.5"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>Yes — email me empty-leg deals and price drops (opt out any time).</span>
      </label>
      {error && (
        <div data-testid="alert-error" className="mono text-[11px]" style={{ color: "rgb(196,52,44)" }}>
          {error}
        </div>
      )}
      <button
        data-testid="alert-submit"
        type="submit"
        disabled={phase === "sending"}
        className={BTN_PRIMARY_CLS}
        style={BTN_SECONDARY_STYLE}
      >
        {phase === "sending" ? "Subscribing…" : "Notify me"}
      </button>
    </form>
  );
}

function PublicLegCard({ el, onReserve }) {
  const slug = el.slug || el.id;
  const cd = countdownTo(el.departure_at);
  return (
    <div className="pj-card pj-card-hover overflow-hidden" data-testid={`public-leg-${el.id}`}>
      {el.image_url && (
        <div className="aspect-[16/9] overflow-hidden" style={{ background: "rgb(var(--surface-2))" }}>
          <img src={el.image_url} alt={el.model_name} loading="lazy" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link to={`/empty-legs/${slug}`} data-testid={`public-leg-link-${el.id}`}>
              <div className="font-display text-[18px] truncate">
                {el.from_airport} → {el.to_airport}
              </div>
            </Link>
            <div className="mono text-[10px] text-[rgb(var(--dim))] capitalize mt-0.5 truncate">
              {el.model_name} · {el.seats} seats
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="mono text-[15px] whitespace-nowrap">{fmtUSD(el.price_usd)}</div>
            {el.discount_pct ? (
              <div data-testid={`public-leg-discount-${el.id}`} className="mono text-[10px] mt-0.5" style={{ color: "rgb(90,120,70)" }}>
                −{el.discount_pct}% vs typical charter
              </div>
            ) : null}
          </div>
        </div>
        <div className="mt-3 mono text-[11px] text-[rgb(var(--dim))]">{fmtTime(el.departure_at)}</div>
        {cd && (
          <div data-testid={`public-leg-countdown-${el.id}`} className="mono text-[10px] mt-1 uppercase tracking-[0.14em] text-[rgb(var(--dim))]">
            {cd}
          </div>
        )}
        <button
          data-testid={`public-leg-reserve-${el.id}`}
          type="button"
          onClick={() => onReserve(el)}
          className={`${BTN_PRIMARY_CLS} mt-4`}
          style={BTN_PRIMARY_STYLE}
        >
          Reserve
        </button>
      </div>
    </div>
  );
}

function PublicShell({ children, testid }) {
  return (
    <div data-testid={testid} className="min-h-screen" style={{ background: "rgb(var(--surface-2))" }}>
      <header className="px-6 py-5 md:px-12 md:py-6 flex items-center justify-between gap-4">
        <Brandmark />
        <a
          href="/widget"
          className="mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--dim))] hover:text-[rgb(var(--fg))]"
        >
          Get a custom quote
        </a>
      </header>
      <main className="px-6 md:px-12 pb-12">{children}</main>
      <footer className="px-6 py-5 md:px-12 md:py-6 mono text-[10px] uppercase tracking-[0.16em] text-[rgb(var(--dim))]">
        14 CFR Part 295 broker · flights operated by FAA-licensed Part 135 carriers
      </footer>
    </div>
  );
}

/* ---------------------------------------------------------------------------
 * PUBLIC /empty-legs — the marketplace list (cards + reserve + alerts).
 * ------------------------------------------------------------------------- */
export function PublicEmptyLegs() {
  const [legs, setLegs] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [reserveLeg, setReserveLeg] = useState(null);
  const attribution = useMemo(readAttribution, []);

  useEffect(() => {
    marketplace
      .list()
      .then((r) => setLegs(r.empty_legs || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <PublicShell testid="page-public-empty-legs">
      <div className="mb-8">
        <div className="pj-label mb-2">· One-way repositioning flights · First come, first flown</div>
        <h1 className="font-display text-[32px] md:text-[40px] leading-[1.02]">Empty-Leg Deals</h1>
        <p className="mono text-[12px] text-[rgb(var(--dim))] mt-2 max-w-xl">
          These jets are flying the route anyway — so the whole cabin sells at a deep
          discount. Each departure exists exactly once.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {legs.map((el) => (
          <PublicLegCard key={el.id} el={el} onReserve={setReserveLeg} />
        ))}
        {loaded && legs.length === 0 && (
          <div data-testid="public-legs-empty" className="col-span-full pj-card p-10 text-center mono text-[12px] text-[rgb(var(--dim))]">
            No live deals right now — subscribe below and we&apos;ll email you the moment one opens.
          </div>
        )}
      </div>

      <div className="max-w-md">
        <PriceAlertForm attribution={attribution} />
      </div>

      {reserveLeg && (
        <ReserveModal leg={reserveLeg} attribution={attribution} onClose={() => setReserveLeg(null)} />
      )}
    </PublicShell>
  );
}

/* ---------------------------------------------------------------------------
 * PUBLIC /empty-legs/:slug — the detail page (client twin of the SSR HTML).
 * ------------------------------------------------------------------------- */
export function PublicEmptyLegDetail() {
  const { slug } = useParams();
  const [leg, setLeg] = useState(null);
  const [gone, setGone] = useState(false);
  const [reserving, setReserving] = useState(false);
  const attribution = useMemo(readAttribution, []);

  useEffect(() => {
    marketplace
      .detail(slug)
      .then((r) => setLeg(r.empty_leg))
      .catch(() => setGone(true));
  }, [slug]);

  if (gone) {
    return (
      <PublicShell testid="page-public-empty-leg-detail">
        <div data-testid="public-leg-gone" className="pj-card p-10 text-center mono text-[12px]">
          This deal is gone — empty legs are one-of-a-kind and sell fast.{" "}
          <Link to="/empty-legs" className="underline">See live deals</Link>
        </div>
      </PublicShell>
    );
  }
  if (!leg) {
    return (
      <PublicShell testid="page-public-empty-leg-detail">
        <div className="mono text-[12px] text-[rgb(var(--dim))]">Loading…</div>
      </PublicShell>
    );
  }

  const cd = countdownTo(leg.departure_at);
  return (
    <PublicShell testid="page-public-empty-leg-detail">
      <div className="max-w-2xl">
        <div className="pj-label mb-2">
          · Empty leg · {cd || fmtTime(leg.departure_at)}
        </div>
        <h1 className="font-display text-[32px] md:text-[40px] leading-[1.02]">
          {leg.from_airport} → {leg.to_airport}
        </h1>
        <div className="mono text-[12px] text-[rgb(var(--dim))] mt-2 capitalize">
          {leg.model_name} · {leg.seats} seats · {fmtTime(leg.departure_at)}
        </div>
        <div className="flex items-baseline gap-3 mt-4">
          <div className="mono text-[24px]" data-testid="public-detail-price">{fmtUSD(leg.price_usd)}</div>
          {leg.discount_pct ? (
            <div className="mono text-[11px]" style={{ color: "rgb(90,120,70)" }}>
              −{leg.discount_pct}% vs typical charter
            </div>
          ) : null}
        </div>
        {leg.image_url && (
          <div className="aspect-[16/9] overflow-hidden pj-card mt-6">
            <img src={leg.image_url} alt={leg.model_name} className="w-full h-full object-cover" />
          </div>
        )}
        <button
          data-testid="public-detail-reserve"
          type="button"
          onClick={() => setReserving(true)}
          className={`${BTN_PRIMARY_CLS} mt-6`}
          style={BTN_PRIMARY_STYLE}
        >
          Reserve this jet
        </button>
        <div className="mt-8">
          <PriceAlertForm
            prefill={{ from_airport: leg.from_airport, to_airport: leg.to_airport }}
            attribution={attribution}
          />
        </div>
      </div>
      {reserving && (
        <ReserveModal leg={leg} attribution={attribution} onClose={() => setReserving(false)} />
      )}
    </PublicShell>
  );
}

/* ---------------------------------------------------------------------------
 * OPS inventory view (auth-gated) — lives at /ops/empty-legs.
 * ------------------------------------------------------------------------- */
export default function EmptyLegs() {
  const [rows, setRows] = useState([]);
  useEffect(() => { ops.emptyLegs().then(setRows).catch(() => {}); }, []);

  return (
    <div data-testid="page-empty-legs" className="p-6 md:p-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div className="min-w-0">
          <div className="pj-label mb-2">· Inventory · Discounted positioning legs</div>
          <h1 className="font-display text-[32px] md:text-[40px] leading-[1.02] truncate">Empty Legs</h1>
        </div>
        <div className="flex-shrink-0">
          <MockBadge />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((el) => (
          <div key={el.id} className="pj-card pj-card-hover overflow-hidden" data-testid={`empty-leg-${el.id}`}>
            {el.image_url && (
              <div className="aspect-[16/9] overflow-hidden" style={{ background: "rgb(var(--surface-2))" }}>
                <img src={el.image_url} alt={el.model_name} loading="lazy" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display text-[16px] truncate" title={el.model_name}>{el.model_name}</div>
                  <div className="mono text-[10px] text-[rgb(var(--dim))] capitalize mt-0.5 truncate">
                    {el.cabin.replace("_"," ")} · {el.seats} seats
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="mono text-[15px] whitespace-nowrap">{fmtUSD(el.price_usd)}</div>
                </div>
              </div>
              <div className="mt-3 mono text-[11px] text-[rgb(var(--dim))] break-words">
                {el.from_airport} → {el.to_airport}
              </div>
              <div className="mono text-[10px] text-[rgb(var(--dim))] mt-1">{fmtTime(el.departure_at)}</div>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <div className="col-span-full pj-card p-10 text-center mono text-[12px] text-[rgb(var(--dim))]">
            No empty legs in inventory. Seed data ships 3 — run <code>python -m seeds.seed</code>.
          </div>
        )}
      </div>
    </div>
  );
}
